<template>
  <div class="page-container">
    <div class="sidebar">
      <Sidebar />
    </div>
    <div class="content-wrapper">
      <!-- Error Alert -->
      <div v-if="error" class="error-alert">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
        <button class="close-alert" @click="error = null">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search and Actions Header -->
      <div class="header-actions">
        <div class="search-container">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search keywords..."
            class="search-input"
            @input="handleSearch"
          />
        </div>
        <button class="add-keyword-btn" @click="openAddModal">
          <i class="fas fa-plus"></i>
          Add Keyword
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading keywords...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="keywords-content">
        <!-- Stats Card -->
        <div class="quick-stats">
          <div class="stat-card">
            <div class="stat-icon total">
              <i class="fas fa-key"></i>
            </div>
            <div class="stat-content">
              <h4>Total Keywords</h4>
              <div class="stat-value">{{ totalKeywords }}</div>
            </div>
          </div>
        </div>

        <!-- Keywords Table Card -->
        <div class="table-card">
          <div class="table-header">
            <h3><i class="fas fa-list"></i> Keyword List</h3>
            <div class="table-actions">
              <!-- Pagination controls -->
              <div class="pagination-controls" v-if="pagination">
                <button 
                  :disabled="currentPage === 1" 
                  @click="changePage(currentPage - 1)"
                  class="pagination-btn"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
                <span class="page-info">Page {{ currentPage }} of {{ pagination.last_page }}</span>
                <button 
                  :disabled="currentPage === pagination.last_page" 
                  @click="changePage(currentPage + 1)"
                  class="pagination-btn"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="table-container">
            <table class="keywords-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Keyword</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="keyword in keywords" :key="keyword.id" class="keyword-row">
                  <td>{{ keyword.id }}</td>
                  <td>
                    <div class="keyword-label">
                      <i class="fas fa-tag keyword-icon"></i>
                      {{ keyword.keyword }}
                    </div>
                  </td>
                  <td>{{ formatDate(keyword.created_at) }}</td>
                  <td>
                    <div class="action-buttons">
                      <button class="action-btn edit" @click="openEditModal(keyword)">
                        <i class="fas fa-edit"></i>
                        <span>Edit</span>
                      </button>
                      <button class="action-btn delete" @click="confirmDelete(keyword)">
                        <i class="fas fa-trash"></i>
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Keyword Modal for Add/Edit -->
      <div v-if="showModal" class="modal-overlay">
        <div class="modal-container">
          <div class="modal-header">
            <h3>{{ selectedKeyword ? 'Edit Keyword' : 'Add New Keyword' }}</h3>
            <button class="close-modal" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label for="keyword">Keyword</label>
              <input 
                type="text" 
                id="keyword" 
                v-model="keywordForm.keyword" 
                placeholder="Enter keyword"
                class="form-input"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn cancel-btn" @click="closeModal">Cancel</button>
            <button class="btn submit-btn" @click="submitKeyword" :disabled="!isFormValid">
              {{ selectedKeyword ? 'Update' : 'Add' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay">
        <div class="modal-container delete-modal">
          <div class="modal-header">
            <h3>Confirm Delete</h3>
            <button class="close-modal" @click="showDeleteModal = false">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete the keyword "{{ keywordToDelete?.keyword }}"?</p>
            <p class="warning-text">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="btn cancel-btn" @click="showDeleteModal = false">Cancel</button>
            <button class="btn delete-btn" @click="deleteKeyword">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'KeywordsList',
  components: {
    Sidebar
  },
  data() {
    return {
      keywords: [],
      pagination: null,
      currentPage: 1,
      perPage: 15,
      loading: true,
      searchQuery: '',
      showModal: false,
      showDeleteModal: false,
      selectedKeyword: null,
      keywordToDelete: null,
      keywordForm: {
        keyword: ''
      },
      error: null,
      searchTimeout: null
    };
  },
  computed: {
    totalKeywords() {
      return this.pagination ? this.pagination.total : 0;
    },
    isFormValid() {
      return this.keywordForm.keyword.trim() !== '';
    }
  },
  created() {
    this.fetchKeywords();
  },
  methods: {
    async fetchKeywords() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          `https://api.kewaar.com/api/admin/keywords?search=${this.searchQuery}&per_page=${this.perPage}&page=${this.currentPage}`, 
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c'}`
            }
          }
        );
        
        this.keywords = response.data.data.data;
        this.pagination = response.data.data;
      } catch (error) {
        this.error = 'Failed to fetch keywords. Please try again.';
        console.error('Error fetching keywords:', error);
      } finally {
        this.loading = false;
      }
    },
    
    handleSearch() {
      // Debounce search to avoid too many API calls
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchKeywords();
      }, 300);
    },
    
    changePage(page) {
      this.currentPage = page;
      this.fetchKeywords();
    },
    
    openAddModal() {
      this.selectedKeyword = null;
      this.keywordForm = { keyword: '' };
      this.showModal = true;
    },
    
    openEditModal(keyword) {
      this.selectedKeyword = keyword;
      this.keywordForm = { keyword: keyword.keyword };
      this.showModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedKeyword = null;
      this.keywordForm = { keyword: '' };
    },
    
    confirmDelete(keyword) {
      this.keywordToDelete = keyword;
      this.showDeleteModal = true;
    },
    
    async submitKeyword() {
      if (!this.isFormValid) return;
      
      try {
        this.error = null;
        const token = localStorage.getItem('token') || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c';
        const headers = {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        
        // Ensure payload matches exactly what the API expects
        const payload = {
          keyword: this.keywordForm.keyword.trim()
        };
        
        if (this.selectedKeyword) {
          // Update existing keyword
          await axios.put(
            `https://api.kewaar.com/api/keywords/${this.selectedKeyword.id}`,
            payload,
            { headers }
          );
        } else {
          // Create new keyword
          await axios.post(
            'https://api.kewaar.com/api/keywords',
            payload,
            { headers }
          );
        }
        
        // Refresh the list
        this.fetchKeywords();
        this.closeModal();
      } catch (error) {
        console.error('Error with keyword operation:', error);
        
        // Extract validation errors if available
        if (error.response && error.response.data) {
          console.log('API Error Response:', error.response.data);
          
          if (error.response.data.errors) {
            // Format validation errors
            const errorMessages = Object.values(error.response.data.errors)
              .flat()
              .join(', ');
            this.error = errorMessages || 'Validation failed';
          } else if (error.response.data.message) {
            this.error = error.response.data.message;
          } else {
            this.error = this.selectedKeyword 
              ? 'Failed to update keyword. Please try again.' 
              : 'Failed to add keyword. Please try again.';
          }
        } else {
          this.error = this.selectedKeyword 
            ? 'Failed to update keyword. Please try again.' 
            : 'Failed to add keyword. Please try again.';
        }
      }
    },
    
    async deleteKeyword() {
      if (!this.keywordToDelete) return;
      
      try {
        this.error = null;
        const token = localStorage.getItem('token') || '5202|e1LubhmY7QxZdkccSw7Iw5bi28ejLZYxV6w2IoWk8e582a2c';
        
        await axios.delete(
          `https://api.kewaar.com/api/keywords/${this.keywordToDelete.id}`,
          {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Refresh the list
        this.fetchKeywords();
        this.showDeleteModal = false;
        this.keywordToDelete = null;
      } catch (error) {
        this.error = 'Failed to delete keyword. Please try again.';
        console.error('Error deleting keyword:', error);
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  height: 100vh;
}

.sidebar {
  width: 250px;
  height: 100%;
}

.content-wrapper {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f5f7fa;
  position: relative;
}

.error-alert {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-alert {
  margin-left: auto;
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.add-keyword-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
}

.add-keyword-btn:hover {
  background-color: #2563eb;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.keywords-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quick-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  font-size: 20px;
}

.stat-icon.total {
  background-color: #dbeafe;
  color: #3b82f6;
}

.stat-content h4 {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

.table-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.table-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pagination-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #4b5563;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #6b7280;
}

.table-container {
  overflow-x: auto;
}

.keywords-table {
  width: 100%;
  border-collapse: collapse;
}

.keywords-table th, 
.keywords-table td {
  padding: 12px 20px;
  text-align: left;
}

.keywords-table th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #4b5563;
  font-size: 14px;
}

.keywords-table tr {
  border-bottom: 1px solid #e5e7eb;
}

.keywords-table tr:last-child {
  border-bottom: none;
}

.keyword-row:hover {
  background-color: #f9fafb;
}

.keyword-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.keyword-icon {
  color: #3b82f6;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}

.action-btn.edit {
  color: #3b82f6;
}

.action-btn.edit:hover {
  background-color: #eff6ff;
}

.action-btn.delete {
  color: #ef4444;
}

.action-btn.delete:hover {
  background-color: #fee2e2;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.delete-modal {
  width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-modal {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6b7280;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4b5563;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
}

.warning-text {
  color: #dc2626;
  font-style: italic;
  margin-top: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.cancel-btn {
  background-color: #f3f4f6;
  color: #4b5563;
}

.submit-btn {
  background-color: #3b82f6;
  color: white;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.delete-btn {
  background-color: #ef4444;
  color: white;
}
</style> 